import moment from 'moment';

import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AnimatedDashboard, ErrorToast, SidebarModal } from '../../components';

import Spinner from '../../components/common/Spinner';
import {
  akashaCoins,
  formatUSD,
  getWalletBalance,
  USDT
} from '../../helpers/helperFunctions';

import ultron from '../../assets/icons/backOffice/Packages/ultron.png';

import { CaptionText, H3, P1 } from '../../styles/FontStyles';
import {
  Button,
  Flexed,
  SectionSeparator,
  Select,
  SelectedUSDT
} from '../../styles/GlobalComponents';
import { color, colors } from '../../styles/GlobalStyles';
import { BoxPortfolio, SummaryGrid } from './Portfolio.styled';
import { DepositUlx } from './UlxDeposit/UlxDeposit';

import { useParams, useHistory, useLocation } from 'react-router-dom';

export const AkashaButton = styled(Button)<{ selected: boolean }>`
  color: ${({ theme }) => theme.colors.neutral900};
  background: ${({ selected }) => (selected ? '#01f1c1' : 'transparent')};
  font-size: 24px;
  height: 60px;
  @media (max-width: 800px) {
    font-size: 18px;
  }
`;

const SummaryGridAkasha = styled(SummaryGrid)`
  grid-template-columns: repeat(4, 1fr);
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(1, 1fr);
  }
  margin: 40px 0;
`;

const AkashaOn = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    left: 50%;
    bottom: -36px;
    transform: translate(-50%, 0);
  }
`;
const AkashaOff = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    top: -24px;
    right: 0px;
  }
`;

const AkashaButtonsWrapper = styled.div`
  width: 50%;
  display: flex;
  box-shadow: 0px 0.67px 0.67px 0px #ffffff inset,
    0px -0.67px 0.53px 0px #00000040 inset, 0px 1.34px 2.37px 0px #00000059,
    0px 0.67px 0.67px 0px #26c8c4;
  border-radius: 8px;

  @media (max-width: 800px) {
    width: 100%;
  }
`;

export const ManageAkashaNode = () => {
  const location = useLocation();
  const [ulx, setUlx] = useState('');
  const [usdt, setUsdt] = useState('');
  const [baseAkashaBalance, setBaseAkashaBalance] = useState<any>(0);
  const [ultronWUlxBalance, setUltronWUlxBalance] = useState<any>(0);

  const history = useHistory();

  const dispatch = useAppDispatch();
  const { loadingSummary } = useAppSelector((state) => state.portfolio);

  const [depositStep, setDepositStep] = useState(
    DepositStepsEnum.COMPLETE_DEPOSIT
  );
  const { userPackage, walletSummary, loading, autoStakeLoading } =
    useAppSelector((state) => state.marketplace);

  const { id } = useParams<{ id: string }>();

  const [selectedToken, setSelectedToken] = useState<TokensType>(
    akashaCoins[0]
  );

  const [nodeOdOff, setNodeOdOff] = useState(false);

  const getWulxUltronBalance = async () => {
    if (!walletSummary || !walletSummary.userAddress) return;

    const wULXAddress = process.env.REACT_APP_WULX_ADDRESS!;
    const wULXDecimals = process.env.REACT_APP_WULX_DECIMALS!;

    const wulxBalance = await getWalletBalance(
      walletSummary?.userAddress,
      wULXAddress,
      wULXDecimals
    );

    setUltronWUlxBalance(wulxBalance);
  };

  const deactivatedSince = () => {
    const now = moment();
    const nextMonday = moment().isoWeekday(8); // Monday of next week

    const diffDays = nextMonday.diff(now, 'days');

    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  };

  const showNodeChangingText = (item: any) => {
    if (item?.claimDate) {
      return (
        <CaptionText mb='20'>
          Node switching change will be processed in <b>{deactivatedSince()}</b>
          .
        </CaptionText>
      );
    }
  };

  useEffect(() => {
    getWulxUltronBalance();
    dispatch(getDepositAkashaInfo({ userPackageId: id }));
  }, [dispatch]);

  return (
    <>
      <AnimatedDashboard zoom>
        <div style={{ marginBottom: 40 }}>
          <ManageAkashaItem
            item={userPackage?.package as any}
            depositedCurrencyAmount={userPackage?.depositedCurrencyAmount ?? 0}
          />

          <SummaryGridAkasha>
            <div>
              <BoxPortfolio>
                <div>
                  <CaptionText
                    caps
                    bold
                    color={color.colors.neutral500}
                    mb='10'>
                    NODE ID NUMBER
                  </CaptionText>

                  <P1 bold flex alignEnd gap='5px'>
                    {userPackage?.id}
                  </P1>
                </div>
              </BoxPortfolio>
            </div>
            <div>
              <BoxPortfolio>
                <div>
                  <CaptionText
                    caps
                    bold
                    color={color.colors.neutral500}
                    mb='10'>
                    NODE PURCHASED DATE
                  </CaptionText>

                  <P1 bold flex alignEnd gap='5px'>
                    {moment(userPackage?.datePaid).format('DD MMM YYYY')}
                  </P1>
                </div>
              </BoxPortfolio>
            </div>
            <div>
              <BoxPortfolio>
                <div>
                  <CaptionText
                    caps
                    bold
                    color={color.colors.neutral500}
                    mb='10'>
                    CURRENT AMOUNT DEPOSITED
                  </CaptionText>

                  <P1 bold flex alignEnd gap='5px'>
                    {formatUSD(userPackage?.depositedUsdtAmount)} {USDT}
                  </P1>
                </div>
              </BoxPortfolio>
              <CaptionText mt='10' ml='10' color={colors.neutral500}>
                {userPackage?.depositedTokenCurrency} Deposit:{' '}
                {formatUSD(userPackage?.depositedCurrencyAmount)}
              </CaptionText>
            </div>
            <div>
              <BoxPortfolio>
                <div>
                  <CaptionText
                    caps
                    bold
                    color={color.colors.neutral500}
                    mb='10'>
                    TOTAL AMOUNT MINTED
                  </CaptionText>

                  <P1 bold flex alignEnd gap='5px'>
                    {formatUSD(userPackage?.mintedAmount)}{' '}
                    {userPackage?.mintedTokenCurrency}
                  </P1>
                </div>
              </BoxPortfolio>
            </div>
          </SummaryGridAkasha>

          <div style={{ position: 'relative' }}>
            {loadingSummary || (loading && <Spinner absolute transparent />)}
          </div>

          <Flexed alignEnd column center>
            <Flexed end>
              <div>{showNodeChangingText(userPackage)}</div>
            </Flexed>
          </Flexed>
          <Flexed between>
            <SelectedUSDT style={{ width: 500 }}>
              {formatUSD(userPackage?.claimableWULXAmount)}
              <b>
                {' '}
                <img
                  src={ultron}
                  alt='checked'
                  style={{ marginRight: 10, width: 14 }}
                />
                wULX
              </b>
            </SelectedUSDT>

            <AkashaButtonsWrapper>
              <AkashaOn>
                <AkashaButton
                  selected={userPackage?.isActive}
                  maxWidth='350px'
                  maxWidthSM='100%'
                  onClick={() => {
                    if (!userPackage?.isActive) {
                      setNodeOdOff(!nodeOdOff);
                    }
                  }}>
                  Node ON
                </AkashaButton>
                <PreferredOptionSvg />
              </AkashaOn>
              <AkashaOff>
                <AkashaButton
                  selected={!userPackage?.isActive}
                  maxWidth='350px'
                  maxWidthSM='100%'
                  onClick={() => {
                    if (userPackage?.isActive) {
                      setNodeOdOff(!nodeOdOff);
                    }
                  }}>
                  Node OFF
                </AkashaButton>
              </AkashaOff>
            </AkashaButtonsWrapper>
          </Flexed>

          <Flexed center justifyCenter>
            <Button
              m='50px 50px'
              maxWidth='350px'
              maxWidthSM='100%'
              disabled={userPackage?.claimableWULXAmount === 0}
              onClick={() => dispatch(claimAkasha({ userPackageId: id }))}>
              Claim
            </Button>
            <Button
              m='50px 0'
              maxWidth='350px'
              maxWidthSM='100%'
              onClick={() => {
                dispatch(getAkashaWalletSummary());
                setDepositStep(DepositStepsEnum.COMPLETE_DEPOSIT);
                dispatch(toggleDepositUlxModal(true));
                setUlx('');
                setUsdt('');
                setBaseAkashaBalance(0);
              }}>
              Complete{' '}
              {userPackage?.depositedTokenCurrency
                ? userPackage?.depositedTokenCurrency
                : selectedToken.token === 'Ultron (wULX)'
                ? 'wULX'
                : 'AK1111'}{' '}
              Deposit
            </Button>
          </Flexed>
        </div>
        <SectionSeparator />
        <H3 color={colors.primary900} mb='24'>
          Super Node Status: ON vs OFF – What It Means
        </H3>
        <P1>
          When you activate your Super Node and set it to <b>ON</b>, you are
          enabling <b>Transaction Mining</b> within the Super Node Network. This
          means your node will actively participate in the ecosystem’s
          operations and share in the profits generated from{' '}
          <b>50% of Super Node’s transaction fees.</b>
          <br />
          <br />
          <ul>
            <li>
              The coin you use to deposit into your node — <b>wULX</b> — will
              also be the <b>coin your node mines.</b>
            </li>
            <li>
              Your deposited amount will begin generating <b>monthly rewards</b>{' '}
              based on your node tier.
            </li>
            <li>
              These earnings are <b>compiled monthly</b> and continue to accrue
              for a<b>10-year period.</b>
            </li>
            <li>
              At the end of each year (based on the anniversary of your initial
              deposit), you may <b>withdraw up to 10%</b> of your total
              accumulated rewards.
            </li>
            <li>
              Please note: The <b>initial deposit</b> into your node is{' '}
              <b>locked for the full 10-year mining period</b> and cannot be
              withdrawn until that time has passed.
            </li>
          </ul>
          <br />
          When your node is set to <b>OFF</b>, it is not participating in
          Transaction Mining and does{' '}
          <b>not earn a share of Super Node’s transaction fees.</b>
          <br />
          <br />
          <ul>
            <li>
              Instead, your node functions in a passive state and earns a{' '}
              <b>flat monthly percentage return</b> on your deposited amount.
            </li>
            <li>
              Unlike the ON setting, these earnings are <b>not compiled</b>{' '}
              long-term and are{' '}
              <b>available for withdrawal at the end of each month.</b>
            </li>
            <li>
              Your node does not <b>accumulate mining rewards</b> while OFF.
            </li>
          </ul>
        </P1>
      </AnimatedDashboard>
      <DepositUlx
        token={
          userPackage?.depositedTokenCurrency === null
            ? selectedToken.token
            : userPackage?.depositedTokenCurrency
        }
        depositStep={depositStep}
        setDepositStep={setDepositStep}
        ulx={ulx}
        usdt={usdt}
        setUlx={setUlx}
        setUsdt={setUsdt}
        baseAkashaBalance={baseAkashaBalance}
        setBaseAkashaBalance={setBaseAkashaBalance}
        ultronWUlxBalance={ultronWUlxBalance}
        setUltronWUlxBalance={setUltronWUlxBalance}
      />

      <SidebarModal
        isOpen={nodeOdOff}
        setOpen={(v) => {
          setNodeOdOff(!nodeOdOff);
        }}
        title={`Are you sure you want to switch your Super Node ${
          !userPackage?.isActive ? 'ON' : 'OFF'
        }?`}
        sidebarWidth={'500px'}>
        {autoStakeLoading && <Spinner transparent absolute />}
        <Desktop>
          <Flexed center between>
            <Button
              m='100px 0'
              maxWidth='200px'
              maxWidthSM='100%'
              negative
              onClick={() => setNodeOdOff(!nodeOdOff)}>
              Cancel
            </Button>
            <Button
              m='100px 0'
              maxWidth='200px'
              maxWidthSM='100%'
              onClick={() => {
                if (userPackage?.isActive !== undefined) {
                  dispatch(
                    autoStakeSuperNode({
                      userPackageId: id!,
                      isActive: !userPackage?.isActive,
                      history
                    })
                  );
                }
              }}>
              Switch {!userPackage?.isActive ? 'ON' : 'OFF'}
            </Button>
          </Flexed>
        </Desktop>
        <Mobile>
          <Button
            m='50px 0 20px 0'
            maxWidth='200px'
            maxWidthSM='100%'
            negative
            onClick={() => setNodeOdOff(!nodeOdOff)}>
            Cancel
          </Button>
          <Button
            maxWidth='200px'
            maxWidthSM='100%'
            onClick={() => {
              if (userPackage?.isActive !== undefined) {
                dispatch(
                  autoStakeSuperNode({
                    userPackageId: id!,
                    isActive: !userPackage?.isActive,
                    history
                  })
                );
              }
            }}>
            Switch {!userPackage?.isActive ? 'ON' : 'OFF'}
          </Button>
        </Mobile>
      </SidebarModal>
    </>
  );
};

import { H4, P2, P3 } from '../../styles/FontStyles';
import { BoxBig, HubImage, Td, Tr } from './Portfolio.styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toggleDepositUlxModal } from '../../slices/portfolio-slice';
import { TokensType } from '../../types/types';
import { Circle } from '../Ranking/RankingStyled';
import { PackageTrading } from './Portfolio.types';

import {
  getDepositAkashaInfo,
  getAkashaWalletSummary,
  autoStakeSuperNode,
  claimAkasha
} from '../Marketplace/Marketplace.actions';
import { DepositStepsEnum } from './UlxDeposit/types';
import { Desktop, Mobile } from '../../App';
import { SelectToken } from './UlxDeposit/SelectNetwork/SelectToken';

export const ManageAkashaItem = ({
  item,
  depositedCurrencyAmount
}: {
  item: PackageTrading;
  depositedCurrencyAmount: number;
}) => {
  const { t } = useTranslation('my_hubs');

  return (
    <>
      <BoxBig relative>
        <HubImage image={item?.image} />

        <div>
          <Flexed center between>
            <H3>My {item?.title}</H3>
            <Flexed center end>
              <P3 bold caps>
                {depositedCurrencyAmount ? t('active') : t('inactive')}
              </P3>

              <Circle
                color={
                  depositedCurrencyAmount
                    ? color.colors.success500
                    : color.colors.error500
                }
              />
            </Flexed>
          </Flexed>
          <div>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                right: '50%',
                transform: 'translate(-50%, -50%)'
              }}>
              <Spinner />
            </div>

            <H4 mb='12'>{item?.name}</H4>
            <P2 mb='32'>{item?.subtitle}</P2>

            <Tr style={{ cursor: 'pointer' }}>
              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Max Deposit Amount
                </CaptionText>
                <P1 bold flex alignEnd gap='5px'>
                  {formatUSD(item?.maxDepositAmount ?? 0)} <P3 mb='3'>USDT</P3>
                </P1>
              </Td>

              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Node on
                </CaptionText>
                <P1 bold>{item?.nodeOnPercentage}%</P1>
              </Td>

              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Node off
                </CaptionText>
                <P1 bold>{item?.nodeOffPercentage}%</P1>
              </Td>
            </Tr>
          </div>
        </div>
      </BoxBig>
    </>
  );
};

const PreferredOptionSvg = () => (
  <svg
    width='132'
    height='26'
    viewBox='0 0 132 26'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M13.144 10.5042C13.7529 10.5042 14.28 10.6057 14.7252 10.8086C15.177 11.0116 15.524 11.2997 15.7663 11.6729C16.0085 12.0461 16.1296 12.488 16.1296 12.9987C16.1296 13.5029 16.0085 13.9448 15.7663 14.3246C15.524 14.6978 15.177 14.9858 14.7252 15.1888C14.28 15.3852 13.7529 15.4834 13.144 15.4834H11.7593V17.3789H10.1683V10.5042H13.144ZM13.0556 14.1871C13.5336 14.1871 13.897 14.0856 14.1458 13.8826C14.3946 13.6731 14.519 13.3785 14.519 12.9987C14.519 12.6124 14.3946 12.3178 14.1458 12.1148C13.897 11.9053 13.5336 11.8005 13.0556 11.8005H11.7593V14.1871H13.0556ZM22.0385 17.3789L20.7127 15.4638H20.6341H19.2494V17.3789H17.6583V10.5042H20.6341C21.243 10.5042 21.7701 10.6057 22.2153 10.8086C22.6671 11.0116 23.0141 11.2997 23.2563 11.6729C23.4986 12.0461 23.6197 12.488 23.6197 12.9987C23.6197 13.5094 23.4953 13.9514 23.2465 14.3246C23.0043 14.6912 22.6573 14.9727 22.2055 15.1692L23.7474 17.3789H22.0385ZM22.0091 12.9987C22.0091 12.6124 21.8847 12.3178 21.6359 12.1148C21.3871 11.9053 21.0237 11.8005 20.5457 11.8005H19.2494V14.1969H20.5457C21.0237 14.1969 21.3871 14.0921 21.6359 13.8826C21.8847 13.6731 22.0091 13.3785 22.0091 12.9987ZM30.5961 16.1022V17.3789H25.2731V10.5042H30.4685V11.7809H26.8543V13.2737H30.0461V14.5112H26.8543V16.1022H30.5961ZM33.8459 11.7809V13.5978H37.0279V14.8745H33.8459V17.3789H32.2549V10.5042H37.4502V11.7809H33.8459ZM44.2432 16.1022V17.3789H38.9202V10.5042H44.1155V11.7809H40.5013V13.2737H43.6932V14.5112H40.5013V16.1022H44.2432ZM50.2821 17.3789L48.9563 15.4638H48.8777H47.4929V17.3789H45.9019V10.5042H48.8777C49.4866 10.5042 50.0137 10.6057 50.4589 10.8086C50.9107 11.0116 51.2577 11.2997 51.4999 11.6729C51.7422 12.0461 51.8633 12.488 51.8633 12.9987C51.8633 13.5094 51.7389 13.9514 51.4901 14.3246C51.2479 14.6912 50.9008 14.9727 50.4491 15.1692L51.991 17.3789H50.2821ZM50.2527 12.9987C50.2527 12.6124 50.1283 12.3178 49.8795 12.1148C49.6307 11.9053 49.2673 11.8005 48.7893 11.8005H47.4929V14.1969H48.7893C49.2673 14.1969 49.6307 14.0921 49.8795 13.8826C50.1283 13.6731 50.2527 13.3785 50.2527 12.9987ZM57.8969 17.3789L56.571 15.4638H56.4925H55.1077V17.3789H53.5167V10.5042H56.4925C57.1014 10.5042 57.6284 10.6057 58.0737 10.8086C58.5254 11.0116 58.8725 11.2997 59.1147 11.6729C59.357 12.0461 59.4781 12.488 59.4781 12.9987C59.4781 13.5094 59.3537 13.9514 59.1049 14.3246C58.8626 14.6912 58.5156 14.9727 58.0638 15.1692L59.6058 17.3789H57.8969ZM57.8674 12.9987C57.8674 12.6124 57.743 12.3178 57.4942 12.1148C57.2454 11.9053 56.882 11.8005 56.4041 11.8005H55.1077V14.1969H56.4041C56.882 14.1969 57.2454 14.0921 57.4942 13.8826C57.743 13.6731 57.8674 13.3785 57.8674 12.9987ZM66.4545 16.1022V17.3789H61.1315V10.5042H66.3268V11.7809H62.7127V13.2737H65.9045V14.5112H62.7127V16.1022H66.4545ZM68.1132 10.5042H71.2363C71.9827 10.5042 72.6408 10.6482 73.2104 10.9363C73.7865 11.2178 74.2318 11.6172 74.546 12.1345C74.8669 12.6517 75.0273 13.2541 75.0273 13.9415C75.0273 14.629 74.8669 15.2314 74.546 15.7486C74.2318 16.2659 73.7865 16.6685 73.2104 16.9566C72.6408 17.2381 71.9827 17.3789 71.2363 17.3789H68.1132V10.5042ZM71.1578 16.0727C71.8452 16.0727 72.392 15.8828 72.7979 15.5031C73.2104 15.1168 73.4166 14.5963 73.4166 13.9415C73.4166 13.2868 73.2104 12.7696 72.7979 12.3898C72.392 12.0035 71.8452 11.8104 71.1578 11.8104H69.7043V16.0727H71.1578ZM83.1235 17.4968C82.4098 17.4968 81.7649 17.3429 81.1888 17.0352C80.6191 16.7274 80.1706 16.3051 79.8433 15.7683C79.5225 15.2248 79.362 14.6159 79.362 13.9415C79.362 13.2672 79.5225 12.6615 79.8433 12.1246C80.1706 11.5812 80.6191 11.1556 81.1888 10.8479C81.7649 10.5402 82.4098 10.3863 83.1235 10.3863C83.8372 10.3863 84.4788 10.5402 85.0484 10.8479C85.6181 11.1556 86.0665 11.5812 86.3939 12.1246C86.7213 12.6615 86.885 13.2672 86.885 13.9415C86.885 14.6159 86.7213 15.2248 86.3939 15.7683C86.0665 16.3051 85.6181 16.7274 85.0484 17.0352C84.4788 17.3429 83.8372 17.4968 83.1235 17.4968ZM83.1235 16.1415C83.5294 16.1415 83.8961 16.0498 84.2235 15.8665C84.5508 15.6766 84.8062 15.4147 84.9895 15.0808C85.1794 14.7469 85.2743 14.3671 85.2743 13.9415C85.2743 13.516 85.1794 13.1362 84.9895 12.8023C84.8062 12.4684 84.5508 12.2098 84.2235 12.0264C83.8961 11.8366 83.5294 11.7416 83.1235 11.7416C82.7176 11.7416 82.3509 11.8366 82.0235 12.0264C81.6962 12.2098 81.4376 12.4684 81.2477 12.8023C81.0644 13.1362 80.9727 13.516 80.9727 13.9415C80.9727 14.3671 81.0644 14.7469 81.2477 15.0808C81.4376 15.4147 81.6962 15.6766 82.0235 15.8665C82.3509 16.0498 82.7176 16.1415 83.1235 16.1415ZM91.4493 10.5042C92.0582 10.5042 92.5853 10.6057 93.0305 10.8086C93.4823 11.0116 93.8293 11.2997 94.0715 11.6729C94.3138 12.0461 94.4349 12.488 94.4349 12.9987C94.4349 13.5029 94.3138 13.9448 94.0715 14.3246C93.8293 14.6978 93.4823 14.9858 93.0305 15.1888C92.5853 15.3852 92.0582 15.4834 91.4493 15.4834H90.0645V17.3789H88.4735V10.5042H91.4493ZM91.3609 14.1871C91.8389 14.1871 92.2022 14.0856 92.451 13.8826C92.6998 13.6731 92.8242 13.3785 92.8242 12.9987C92.8242 12.6124 92.6998 12.3178 92.451 12.1148C92.2022 11.9053 91.8389 11.8005 91.3609 11.8005H90.0645V14.1871H91.3609ZM97.1958 11.8005H94.9959V10.5042H100.987V11.8005H98.7869V17.3789H97.1958V11.8005ZM102.236 10.5042H103.827V17.3789H102.236V10.5042ZM109.18 17.4968C108.467 17.4968 107.822 17.3429 107.246 17.0352C106.676 16.7274 106.228 16.3051 105.9 15.7683C105.579 15.2248 105.419 14.6159 105.419 13.9415C105.419 13.2672 105.579 12.6615 105.9 12.1246C106.228 11.5812 106.676 11.1556 107.246 10.8479C107.822 10.5402 108.467 10.3863 109.18 10.3863C109.894 10.3863 110.536 10.5402 111.105 10.8479C111.675 11.1556 112.123 11.5812 112.451 12.1246C112.778 12.6615 112.942 13.2672 112.942 13.9415C112.942 14.6159 112.778 15.2248 112.451 15.7683C112.123 16.3051 111.675 16.7274 111.105 17.0352C110.536 17.3429 109.894 17.4968 109.18 17.4968ZM109.18 16.1415C109.586 16.1415 109.953 16.0498 110.28 15.8665C110.608 15.6766 110.863 15.4147 111.046 15.0808C111.236 14.7469 111.331 14.3671 111.331 13.9415C111.331 13.516 111.236 13.1362 111.046 12.8023C110.863 12.4684 110.608 12.2098 110.28 12.0264C109.953 11.8366 109.586 11.7416 109.18 11.7416C108.774 11.7416 108.408 11.8366 108.08 12.0264C107.753 12.2098 107.494 12.4684 107.305 12.8023C107.121 13.1362 107.03 13.516 107.03 13.9415C107.03 14.3671 107.121 14.7469 107.305 15.0808C107.494 15.4147 107.753 15.6766 108.08 15.8665C108.408 16.0498 108.774 16.1415 109.18 16.1415ZM120.836 10.5042V17.3789H119.529L116.102 13.205V17.3789H114.53V10.5042H115.846L119.264 14.6781V10.5042H120.836Z'
      fill='url(#paint0_linear_20930_14893)'
    />
    <rect
      x='0.751771'
      y='1.5213'
      width='129.692'
      height='23.6982'
      rx='11.8491'
      stroke='url(#paint1_linear_20930_14893)'
      strokeWidth='1.33167'
    />
    <defs>
      <linearGradient
        id='paint0_linear_20930_14893'
        x1='-0.781853'
        y1='4.59629'
        x2='113.03'
        y2='56.0279'
        gradientUnits='userSpaceOnUse'>
        <stop offset='0.00905331' stopColor='#00D3A9' />
        <stop offset='0.383557' stopColor='#007E65' />
        <stop offset='0.563708' stopColor='#084438' />
        <stop offset='0.817388' stopColor='#00B08D' />
        <stop offset='1' stopColor='#001724' />
      </linearGradient>
      <linearGradient
        id='paint1_linear_20930_14893'
        x1='-11.2561'
        y1='-4.94858'
        x2='138.225'
        y2='32.6024'
        gradientUnits='userSpaceOnUse'>
        <stop offset='0.00905331' stopColor='#00D3A9' />
        <stop offset='0.383557' stopColor='#007E65' />
        <stop offset='0.563708' stopColor='#084438' />
        <stop offset='0.817388' stopColor='#00B08D' />
        <stop offset='1' stopColor='#001724' />
      </linearGradient>
    </defs>
  </svg>
);
function useMatch(arg0: string) {
  throw new Error('Function not implemented.');
}
